'use client';

import { useState } from 'react';
import { FEEDBACK_MAX_LEN } from '@shared/services/feedback';

// Блок «Не нашли ответ?» внизу /faq. Стиль 1:1 со светлыми формами
// (EmailLoginForm) — новой визуальной системы не вводим.
//
// Залогиненный → поле вопроса. Аноним → email + вопрос: отправка заводит
// аккаунт по email (мы отвечаем только зарегистрированным) и пересылает вопрос
// разработчику. Оба пути бьют в POST /api/feedback.

const SUPPORT_EMAIL = 'hello@favbox.app';

const inputCls =
  'w-full px-4 py-3 rounded-xl bg-surface border border-line text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent transition-colors';
const buttonCls =
  'w-full px-4 py-3 min-h-[44px] rounded-xl bg-accent hover:bg-accent/90 disabled:opacity-60 text-accent-fg text-sm font-medium transition-colors cursor-pointer';

export default function FeedbackForm({ isLoggedIn }: { isLoggedIn: boolean }) {
  return isLoggedIn ? <LoggedInForm /> : <AnonForm />;
}

function LoggedInForm() {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setError('');
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      const data: { ok?: boolean; error?: string } = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setStatus('sent');
        return;
      }
      setError(data.error || 'Не удалось отправить. Попробуй ещё раз.');
      setStatus('error');
    } catch {
      setError('Нет связи с сервером. Проверь интернет и попробуй ещё раз.');
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <div role="status" aria-live="polite" className="text-sm text-secondary leading-relaxed">
        <span className="font-medium text-primary">Спасибо!</span> Сообщение отправлено разработчику.
        Если вопрос требует ответа — он свяжется с тобой.
      </div>
    );
  }

  return (
    <>
      <p className="text-sm text-secondary leading-relaxed">
        Задай вопрос — сообщение попадёт разработчику Favbox. Если проблема связана с конкретной
        записью, категорией или сообщением бота, упомяни это в тексте.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-2.5">
        <textarea
          required
          rows={4}
          maxLength={FEEDBACK_MAX_LEN}
          placeholder="Опиши вопрос или проблему…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`${inputCls} resize-y`}
        />
        <button type="submit" disabled={status === 'sending' || message.trim().length === 0} className={buttonCls}>
          {status === 'sending' ? 'Отправляем…' : 'Отправить'}
        </button>
        {error && <p className="text-xs text-amber-500" role="alert">{error}</p>}
      </form>
    </>
  );
}

function AnonForm() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setError('');
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, message }),
      });
      const data: { ok?: boolean; error?: string } = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setStatus('sent');
        return;
      }
      setError(data.error || 'Не удалось отправить. Попробуй ещё раз.');
      setStatus('error');
    } catch {
      setError('Нет связи с сервером. Проверь интернет и попробуй ещё раз.');
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <div role="status" aria-live="polite" className="text-sm text-secondary leading-relaxed">
        <span className="font-medium text-primary">Спасибо!</span> Вопрос отправлен — ответим на{' '}
        {email}. Заодно завели тебе аккаунт на этот адрес: войти в каталог можно входом по email.
      </div>
    );
  }

  return (
    <>
      <p className="text-sm text-secondary leading-relaxed">
        Оставь email и вопрос. Мы отвечаем только зарегистрированным пользователям, поэтому вместе с
        отправкой на этот адрес <span className="text-primary font-medium">заведём тебе аккаунт</span>{' '}
        (войти в каталог сможешь потом входом по email). Или напиши на{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-accent hover:underline">
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
      <form onSubmit={submit} className="flex flex-col gap-2.5">
        <input
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
        />
        <textarea
          required
          rows={4}
          maxLength={FEEDBACK_MAX_LEN}
          placeholder="Опиши вопрос или проблему…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`${inputCls} resize-y`}
        />
        <button
          type="submit"
          disabled={status === 'sending' || email.trim().length === 0 || message.trim().length === 0}
          className={buttonCls}
        >
          {status === 'sending' ? 'Отправляем…' : 'Отправить вопрос'}
        </button>
        {error && <p className="text-xs text-amber-500" role="alert">{error}</p>}
      </form>
    </>
  );
}
